/**
 * Versión y notas de la app. Subí APP_VERSION cada vez que liberes cambios
 * y sumá una entrada arriba de todo en CHANGELOG.
 *
 * Al armar la web, las últimas entradas van a version.json: quien tenga la
 * web abierta con una versión anterior ve el aviso con lo que trae (los
 * `cambios`, cortos) y el `aviso` si lo hay. Con `obligatoria`, el aviso no
 * se puede cerrar.
 */

export const APP_VERSION = '1.5.4';

export interface PedidoResuelto {
  pedido: string; // lo que pidieron, en sus palabras
  hicimos: string; // cómo se resolvió
}

export interface ReleaseNote {
  version: string;
  fecha: string; // DD/MM/YYYY
  titulo: string;
  cambios?: string[];
  pedidos?: PedidoResuelto[]; // formato "Pidieron → Hicimos"
  aviso?: string; // un mensaje para todos, destacado en el aviso de versión nueva
  obligatoria?: boolean; // el aviso de versión nueva no se puede cerrar
}

export const CHANGELOG: ReleaseNote[] = [
  {
    version: '1.5.4',
    fecha: '14/10/2026',
    titulo: 'Rendir solo un lapso',
    cambios: [
      'Petshops: en «Ventas y rendiciones», con un lapso elegido, «Rendir lo elegido» rinde solo las ventas pendientes de esas fechas y horas.',
      'Rendir: al terminar, el mensaje dice bien cuántas ventas se rindieron.',
    ],
  },
  {
    version: '1.5.3',
    fecha: '14/10/2026',
    titulo: 'Petshops: por producto y con hora',
    cambios: [
      'Petshops: «Por producto» muestra cuántas unidades se vendieron de cada producto en el lapso. Tocás uno y ves sus ventas con fecha y hora.',
      'Petshops: al elegir las fechas también podés escribir la hora, para contar justo desde la última visita.',
    ],
  },
  {
    version: '1.5.2',
    fecha: '14/10/2026',
    titulo: 'Arreglo en Petshops',
    cambios: ['Petshops: arriba, las «ventas sin rendir» ya no cuentan las anuladas (igual que el total).'],
    aviso: 'Desde ahora, cada vez que haya una versión nueva de la web te va a salir este aviso. Tocá «Actualizar» para tenerla.',
  },
  {
    version: '1.5.1',
    fecha: '14/10/2026',
    titulo: 'Petshops más ordenado',
    cambios: [
      'Petshops: el TOTAL va en la primera fila de la tabla, justo arriba de la primera venta.',
      'Petshops: las fechas se eligen tocando el calendario, como en la app.',
      'Petshops: «Evaluar ventas» tiene la misma tabla que «Ventas y rendiciones».',
    ],
  },
  {
    version: '1.5.0',
    fecha: '14/10/2026',
    titulo: 'Petshops por fechas y aviso de versión nueva',
    cambios: [
      'Petshops: en «Ventas y rendiciones» y en «Evaluar ventas» elegís el lapso (fechas, o Hoy, Ayer, Semana, Mes, Año y Todo).',
      'Petshops: el total del lapso, con cuánto fue en efectivo y cuánto por QR.',
      'Petshops: el Excel y el PDF bajan el lapso elegido, con lo vendido de cada producto.',
      'Petshops: «Ventas y rendiciones» ya no cuenta como pendientes las ventas que ya se rindieron.',
      'Petshops: los botones Excel y PDF de arriba vuelven a bajar el archivo.',
      'Cuando sale una versión nueva de la web, aparece un aviso con lo que trae y el botón «Actualizar».',
      'Petshops: cada petshop se agrega y se edita desde acá (nombre, nombre corto, dueño, teléfono y QR).',
      'Rendir: muestra el QR para pagar y manda el comprobante por WhatsApp.',
      'Solicitudes: al entregar se respetan las cantidades que cambiás, y la entrega queda en el historial del stock.',
      'Solicitudes: al crear un insumo nuevo, avisa si ya hay uno parecido.',
    ],
  },
  {
    version: '1.4.0',
    fecha: '26/06/2026',
    titulo: 'Control y trazabilidad de stock',
    pedidos: [
      {
        pedido: 'Quiero ver el historial del stock: con cuánto se creó un producto, cuándo se repuso, cuándo se vendió o usó.',
        hicimos: 'Cada producto de Petshop, Farmacia e Insumos tiene un botón de reloj 🕐. Ahí ves todo lo que le pasó al stock: creación, reposición, venta, uso y anulación — con fecha, cantidad, con cuánto quedó y quién lo hizo.',
      },
      {
        pedido: 'Que una vez hecha la venta del petshop no se pueda cambiar el método de pago.',
        hicimos: 'El método de pago queda fijo apenas se registra la venta. Si hubo un error, se anula la venta y se vuelve a cargar (así queda todo con control).',
      },
      {
        pedido: 'Poder anular una venta o un uso que se registró por error, que devuelva el stock y no quede como plata que no entró.',
        hicimos: 'Botón "Anular" en Petshop, Farmacia e Insumos: devuelve el stock, quita el ingreso y deja registrado el motivo, quién y cuándo (no se borra, queda el rastro). Cualquiera puede anular.',
      },
    ],
  },
  {
    version: '1.3.1',
    fecha: '18/06/2026',
    titulo: 'Arreglos en Solicitudes',
    pedidos: [
      {
        pedido: 'Una doctora hizo un pedido y salió a nombre de otra.',
        hicimos: 'Ahora el "Pedido por" es quien envía la solicitud, no quien dejó armado el borrador del área. Corregimos también los pedidos que habían quedado mal.',
      },
      {
        pedido: 'Si toco "Empezar lista" y cancelo sin agregar nada, no debería quedar una lista vacía dando vueltas.',
        hicimos: 'La lista recién se crea cuando agregás el primer ítem. Si cancelás sin nada, no queda ningún borrador fantasma.',
      },
    ],
  },
  {
    version: '1.3.0',
    fecha: '11/06/2026',
    titulo: 'Jaulas, petshop y arreglos',
    pedidos: [
      {
        pedido: 'Necesitamos llevar las jaulas que prestamos: a quién, hasta cuándo, con foto, y que avise cuando vence.',
        hicimos: 'Nuevo módulo "Jaulas": cada jaula con foto, color y tamaño; se presta anotando cliente y celular (con botón de WhatsApp), desde y hasta; aviso cuando una vence; descripción/estado y garantía; e historial de préstamos con Excel y PDF.',
      },
      {
        pedido: 'Los gastos salen con la fecha y hora equivocadas.',
        hicimos: 'Corregimos la zona horaria: ahora la fecha y la hora son las reales de cuando se registró.',
      },
      {
        pedido: 'Quiero evaluar las ventas del petshop por período, no solo del mes.',
        hicimos: 'Botones rápidos Hoy/Ayer/Semana/Mes/Año + rango de fechas, mostrando la lista de ventas y con exportación a Excel.',
      },
      {
        pedido: 'Que no se pueda vender del petshop algo que no tiene stock.',
        hicimos: 'La venta descuenta el stock automáticamente y no deja vender si no alcanza.',
      },
    ],
  },
  {
    version: '1.2.0',
    fecha: '19/05/2026',
    titulo: 'Pagos, solicitudes y estadísticas',
    cambios: [
      'Gastos: opción "pendiente de pago" que no suma al total hasta marcarlo pagado.',
      'Gastos: adjuntar comprobante o recibo a cada pago.',
      'Gastos: filtros por proveedor, estado de pago y fechas (automáticos).',
      'Gastos: formulario rediseñado con monto destacado y fecha junto al método de pago.',
      'Solicitudes: recepción ahora puede pedir productos de Farmacia.',
      'Solicitudes: entrega parcial — entregás parte y el resto queda pendiente.',
      'Solicitudes: buscador de productos al armar el pedido + botón "Entregar directo".',
      'Solicitudes: filtros por área y fecha para la admin.',
      'Inventario: botones Usar/Salida y Avisar siempre en su lugar; gris cuando no hay stock.',
      'Inventario: el aviso de reposición salta cuando el stock está bajo, no solo en cero.',
      'Estadísticas (nuevo): gráficas de gastos, categorías, proveedores, método de pago y petshops.',
      'Doctora: se quitó el botón de WhatsApp y Archivos clínicos de su menú.',
    ],
  },
  {
    version: '1.1.0',
    fecha: '19/05/2026',
    titulo: 'Formularios, petshops y proveedores',
    cambios: [
      'Nuevo módulo Formularios: stock de papeles preimpresos (castración, vacuna, autorizaciones).',
      'Petshops: resumen diario y mensual para el cierre de caja.',
      'Petshops: venta tipo carrito con varios productos y cantidades.',
      'Proveedores: horario de atención y días que no atienden.',
      'Solicitudes por área con borrador compartido entre doctoras.',
      'Tablas rediseñadas con columnas más claras y stock visible.',
    ],
  },
  {
    version: '1.0.0',
    fecha: '13/05/2026',
    titulo: 'Primera versión',
    cambios: [
      'Inventario de Farmacia e Insumos internos.',
      'Gastos de la clínica.',
      'Petshops Danna y Bruno con catálogo y ventas.',
      'Gestión de proveedores y usuarios por rol.',
    ],
  },
];
